import { permitFieldDisplayOrder } from '@contracts/order/fields/PermitFieldDisplayOrder';
import { AxleFormName } from '@src/permitField/AxleInformationFields';
import { TrailersFormName } from '@src/permitField/TrailerInformationFields';

type Section = {
  [key: string]: string[];
};

export type CategoryKey = keyof typeof permitFieldDisplayOrder;

const {
  carrierAndPermitDelivery,
  accountsAndInsurances,
  haulingEquipment,
  loadInformation,
  axlesConfiguration,
  routingAndStates,
  notesAndDocuments,
} = permitFieldDisplayOrder;

export const fields = {
  carrierAndPermitDelivery: {
    companyInformation: [...carrierAndPermitDelivery.companyInformation],
    companyAddress: [...carrierAndPermitDelivery.companyAddress],
    driverInformation: [...carrierAndPermitDelivery.driverInformation],
    orderInformation: [...carrierAndPermitDelivery.orderInformation],
    permitDeliveryAndNotifications: [
      ...carrierAndPermitDelivery.permitDeliveryAndNotifications,
    ],
  },
  loadInformation: {
    loadInformation: [...loadInformation.loadInformation],
    mobileHome: [...loadInformation.mobileHome],
  },
  haulingEquipment: {
    tractorInformation: [...haulingEquipment.tractorInformation],
    tractorDimensions: [...haulingEquipment.tractorDimensions],
    tractorLicense: [...haulingEquipment.tractorLicense],
    tractorOwner: [...haulingEquipment.tractorOwner],
    globalTrailers: [...haulingEquipment.globalTrailers],
    trailers: { [TrailersFormName]: [...haulingEquipment.trailers] },
  },
  axlesConfiguration: {
    axles: { [AxleFormName]: [...axlesConfiguration.axles] },
    overallDimensions: [...axlesConfiguration.overallDimensions],
  },
  routingAndStates: {
    globalTrips: [...routingAndStates.globalTrips],
    trips: [...routingAndStates.trips],
  },
  accountsAndInsurances: {
    account: [...accountsAndInsurances.account],
    insuranceInformation: [...accountsAndInsurances.insuranceInformation],
    juridictionIds: [...accountsAndInsurances.juridictionIds],
  },
  notesAndDocuments: {
    comments: [...notesAndDocuments.comments],
  },
};

export const categories = Object.keys(fields) as (keyof typeof fields)[];

const getAllFieldKeys = (): string[] => {
  const keys: string[] = [];
  const categories = Object.keys(fields) as (keyof typeof fields)[];

  categories.forEach((categoryKey) => {
    const categorySections = fields[categoryKey];

    Object.entries(categorySections).forEach(([sectionKey, sectionFields]) => {
      if (Array.isArray(sectionFields)) {
        keys.push(...sectionFields);
      } else {
        keys.push(sectionKey);
        const nestedSectionFields = Object.values(
          sectionFields as Record<string, string[]>,
        ).flatMap((keys) => keys);
        keys.push(...nestedSectionFields);
      }
    });
  });

  return keys;
};

export const getFieldsBySectionsInCategory = (
  categoryName: keyof typeof fields,
) => {
  const category = fields[categoryName];
  const sections = Object.keys(category) as (keyof typeof category)[];

  return new Map<string, string[] | Section>(
    sections.map((section) => [section, category[section]]),
  );
};

export const allFieldKeys = getAllFieldKeys();
